import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Package, ShoppingCart, Settings, Key, Eye, EyeOff, Activity, Zap, LogOut, Loader2, AlertCircle, BrainCircuit, ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';
import { LunaVoiceModel } from './LunaVoiceModel';

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  stockCount: number;
}

export default function StoreDashboard({ onExit, storeApiKey, storeName }: { onExit?: () => void, storeApiKey: string, storeName: string }) {
  const [showApiKey, setShowApiKey] = useState(false);
  const apiKey = storeApiKey;

  const [inventory, setInventory] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', category: '', price: 0, stockCount: 0 });
  const [toast, setToast] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'inventory' | 'live-sales'>('inventory');
  const [cart, setCart] = useState<{product: Product, qty: number}[]>([]);

  const addToCart = (product: Product, qty: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        const newQty = existing.qty + qty;
        if (newQty > product.stockCount) return prev;
        return prev.map(item => item.product.id === product.id ? {...item, qty: newQty} : item);
      }
      return [...prev, {product, qty}];
    });
  };

  const processTransaction = async () => {
    try {
      for (const item of cart) {
        const response = await fetch('/api/pos-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey, productId: item.product.id, quantitySold: item.qty })
        });
        if (!response.ok) throw new Error(`Failed to sell ${item.product.name}`);
      }
      setToast('Transaction Successful!');
      setTimeout(() => setToast(null), 3000);
      setCart([]);
      getInventoryData();
    } catch (err) {
      console.error(err);
      setError('Transaction failed');
    }
  };

  const grandTotal = cart.reduce((sum, item) => sum + item.product.price * item.qty, 0);

  const getInventoryData = async () => {
    try {
      setIsLoading(true);
      // CRITICAL FIX: Use relative path. In a cloud environment, 'localhost' 
      // refers to the user's local machine, not the cloud server.
      const response = await fetch(`/api/inventory?apiKey=${apiKey}`);
      
      if (!response.ok) {
        // Extract the exact error message from the backend if available
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to fetch inventory data'}`);
      }
      
      const data = await response.json();
      // The backend returns { store: string, inventory: Product[] }
      setInventory(data.inventory || []);
    } catch (err) {
      console.error("Fetch Error:", err);
      // Surface the exact error message to the UI
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInventoryData();
  }, []);

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/add-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newProduct, apiKey })
      });
      if (!response.ok) throw new Error('Failed to add product');
      
      setToast('Product added successfully!');
      setTimeout(() => setToast(null), 3000);
      setIsModalOpen(false);
      setNewProduct({ name: '', category: '', price: 0, stockCount: 0 });
      getInventoryData();
    } catch (err) {
      console.error(err);
      setError('Failed to add product');
    }
  };

  const activeProducts = inventory.length;
  const lowStockAlerts = inventory.filter(item => item.stockCount <= 10).length;

  const getStatus = (stock: number) => {
    if (stock > 10) return 'Optimal';
    if (stock > 0 && stock <= 10) return 'Low';
    return 'Depleted';
  };

  return (
    <>
      <div className="min-h-screen bg-black text-white flex overflow-hidden font-sans selection:bg-cyan-500/30">
        {/* Background Effects */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-900/20 via-black to-black"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/10 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/10 blur-[120px] rounded-full"></div>
        </div>

        {/* Sidebar */}
        <aside className="w-64 border-r border-white/10 bg-black/50 backdrop-blur-xl z-10 flex flex-col">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="text-cyan-400" size={24} />
              <span className="text-xl font-black tracking-widest bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">POS.OS</span>
            </div>
            {onExit && (
              <button onClick={onExit} className="text-slate-400 hover:text-white transition-colors" title="Exit Dashboard">
                <LogOut size={18} />
              </button>
            )}
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {[
              { icon: LayoutDashboard, label: 'Overview', active: activeView === 'inventory' },
              { icon: Package, label: 'Inventory Matrix', onClick: () => setActiveView('inventory') },
              { icon: ShoppingCart, label: 'Live Sales', onClick: () => setActiveView('live-sales') },
              { icon: Activity, label: 'Analytics' },
              { icon: Settings, label: 'Settings' },
            ].map((item, idx) => (
              <button key={idx} onClick={item.onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${item.active ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                <item.icon size={18} />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-cyan-500 to-purple-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]"></div>
              <div className="text-sm">
                <p className="font-bold text-white">{storeName}</p>
                <p className="text-slate-400 text-xs">Connected</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 z-10 overflow-y-auto">
          <header className="flex justify-between items-center mb-10">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Command Center</h1>
              <p className="text-slate-400">Welcome back, Commander. System is nominal.</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-4 py-2 rounded-full border border-green-500/30 bg-green-500/10 text-green-400 text-sm font-mono flex items-center gap-2 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                SYNC ACTIVE
              </div>
            </div>
          </header>

          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { label: 'Total Revenue', value: 'Rs. 245,000', trend: '+12.5%', color: 'cyan' },
              { label: 'Active Products', value: activeProducts.toString(), trend: '+0', color: 'purple' },
              { label: 'Low Stock Alerts', value: lowStockAlerts.toString(), trend: '-0', color: 'red' },
            ].map((stat, idx) => (
              <div key={idx} className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md relative overflow-hidden group hover:border-white/20 transition-all">
                <div className={`absolute top-0 right-0 w-32 h-32 bg-${stat.color}-500/10 blur-3xl rounded-full group-hover:bg-${stat.color}-500/20 transition-all`}></div>
                <p className="text-slate-400 text-sm font-medium mb-2">{stat.label}</p>
                <div className="flex items-end justify-between">
                  <h3 className="text-3xl font-bold text-white">{stat.value}</h3>
                  <span className={`text-sm font-mono ${stat.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>{stat.trend}</span>
                </div>
              </div>
            ))}
          </div>

          {/* API Key Zone */}
          <div className="mb-10 p-6 rounded-2xl border border-cyan-500/30 bg-cyan-950/20 backdrop-blur-md flex items-center justify-between shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-cyan-500/20 text-cyan-400">
                <Key size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Store API Key</h3>
                <p className="text-slate-400 text-sm">Use this key to authenticate your POS system with the Big Brain.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <code className="px-4 py-2 rounded-lg bg-black/50 border border-white/10 font-mono text-cyan-400 tracking-wider">
                {showApiKey ? apiKey : '••••••••••••••••••••'}
              </code>
              <button 
                onClick={() => setShowApiKey(!showApiKey)}
                className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              >
                {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* View Switcher */}
          {activeView === 'inventory' ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md overflow-hidden min-h-[300px] flex flex-col">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">Inventory Matrix</h3>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium transition-colors shadow-[0_0_15px_rgba(6,182,212,0.3)]"
                >
                  Add Product
                </button>
              </div>
              
              <div className="flex-1 relative">
                {isLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 backdrop-blur-sm">
                    <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mb-4" />
                    <p className="text-cyan-400 font-mono text-sm tracking-widest animate-pulse">SCANNING DATABASE...</p>
                  </div>
                ) : error ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-950/20 backdrop-blur-sm">
                    <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
                    <p className="text-red-400 font-mono text-sm tracking-widest">{error}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-white/10 text-slate-400 text-sm">
                          <th className="p-4 font-medium">Product ID</th>
                          <th className="p-4 font-medium">Name</th>
                          <th className="p-4 font-medium">Category</th>
                          <th className="p-4 font-medium">Price</th>
                          <th className="p-4 font-medium">Stock</th>
                          <th className="p-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {inventory.map((item) => {
                          const status = getStatus(item.stockCount);
                          return (
                            <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                              <td className="p-4 font-mono text-slate-400">PRD-{item.id.toString().padStart(3, '0')}</td>
                              <td className="p-4 font-medium text-white">{item.name}</td>
                              <td className="p-4 text-slate-400">{item.category}</td>
                              <td className="p-4 text-slate-300">Rs. {Number(item.price).toLocaleString()}</td>
                              <td className="p-4 font-mono">{item.stockCount}</td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  status === 'Optimal' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                                  status === 'Low' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                                  'bg-red-500/10 text-red-400 border border-red-500/20'
                                }`}>
                                  {status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {inventory.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-500 font-mono">
                              NO INVENTORY DATA FOUND
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Product Selector */}
              <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6">
                <h3 className="text-xl font-bold text-white mb-6">Product Selector</h3>
                <div className="grid grid-cols-2 gap-4">
                  {inventory.map(item => (
                    <button key={item.id} onClick={() => addToCart(item, 1)} className="p-4 rounded-xl bg-black/50 border border-white/10 hover:border-cyan-500/50 transition-all text-left">
                      <p className="font-bold text-white">{item.name}</p>
                      <p className="text-slate-400 text-xs">Rs. {item.price} | Stock: {item.stockCount}</p>
                    </button>
                  ))}
                </div>
              </div>
              {/* Bill/Cart */}
              <div className="rounded-2xl border border-cyan-500/30 bg-cyan-950/10 backdrop-blur-md p-6 flex flex-col shadow-[0_0_30px_rgba(6,182,212,0.1)]">
                <h3 className="text-xl font-bold text-white mb-6">Receipt Panel</h3>
                <div className="flex-1 space-y-4 mb-6">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.product.name} x {item.qty}</span>
                      <span>Rs. {item.product.price * item.qty}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-4 mb-6">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Grand Total</span>
                    <span className="text-cyan-400">Rs. {grandTotal}</span>
                  </div>
                </div>
                <button onClick={processTransaction} className="w-full p-4 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors shadow-[0_0_20px_rgba(6,182,212,0.4)]">
                  PROCESS TRANSACTION
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
      
      {isModalOpen && (
        <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md">
          <div className="bg-[#0a0f16]/95 border border-cyan-500/40 rounded-2xl p-8 w-full max-w-md shadow-[0_0_30px_rgba(0,255,255,0.1)]">
            <h2 className="text-2xl font-bold text-white mb-6">Add New Product</h2>
            <form onSubmit={handleAddProduct} className="flex flex-col gap-4">
              <input type="text" placeholder="Name" className="w-full p-3 rounded-xl bg-black/50 border border-white/10 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500" value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} required />
              <input type="text" placeholder="Category" className="w-full p-3 rounded-xl bg-black/50 border border-white/10 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500" value={newProduct.category} onChange={e => setNewProduct({...newProduct, category: e.target.value})} required />
              <input type="number" placeholder="Price" className="w-full p-3 rounded-xl bg-black/50 border border-white/10 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500" value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: Number(e.target.value)})} required />
              <input type="number" placeholder="Stock" className="w-full p-3 rounded-xl bg-black/50 border border-white/10 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500" value={newProduct.stockCount} onChange={e => setNewProduct({...newProduct, stockCount: Number(e.target.value)})} required />
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-colors border border-white/10">Cancel</button>
                <button type="submit" className="flex-1 p-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors shadow-[0_0_15px_rgba(6,182,212,0.3)]">Add Product</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Store Luna Integration */}
      <div className="fixed bottom-0 right-0 z-50 origin-bottom-right transition-transform duration-300 scale-50 opacity-90 hover:opacity-100 hover:scale-[0.55]">
        <div className="bg-black/60 backdrop-blur-xl border border-purple-500/30 rounded-[3rem] p-12 shadow-[0_0_50px_rgba(168,85,247,0.2)]">
          <div className="w-[600px] h-[600px] flex items-center justify-center">
            <LunaVoiceModel apiKey={apiKey} role="store" />
          </div>
        </div>
      </div>
    </>
  );
}
